import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  // Placeholder character to temporarily replace colons during translation
  // Using Unicode replacement character (U+FFFD) as it's unlikely to appear in service names
  private readonly COLON_PLACEHOLDER = '\uFFFD';

  constructor() {}

  /**
   * Translates a string ID using Angular's $localize function.
   *
   * IMPORTANT: This method handles service names and other dynamic content that may contain
   * colons (:). Angular's $localize uses colons as delimiters in its message format:
   *   :@@messageId:default text
   *
   * When content contains colons (e.g., timestamps like "Service - 11:28:27"), the $localize
   * parser misinterprets them as structural delimiters, causing "ghost numbers" to appear
   * as prefixes (e.g., "28:27:Service Name").
   *
   * This issue was originally fixed in TOBE-12725 (August 2023) by removing client-side
   * translation, but regressed in September 2025 when this centralized translation function
   * was introduced (TOBE-16599).
   *
   * SOLUTION: Temporarily escape colons before passing to $localize, then restore them
   * in the translated result.
   *
   * @param id - The string to translate (may contain colons or other special characters)
   * @returns Translated string with colons preserved, or original string if no translation found
   * @see TOBE-12725 - Original ghost numbers bug
   * @see TOBE-16599 - Translation feature that caused regression
   */
  localizeId(id?: any): string {
    if (!id) {
      return $localize`:@@none:none` as string;
    }

    if (typeof id !== 'string') {
      return this.resolveNonStringInput(id);
    }

    // Escape colons to prevent $localize parsing issues
    const sanitized = id.replace(/:/g, this.COLON_PLACEHOLDER);
    const match = this.tryTranslationWithFallback(sanitized);

    if (match === null) return id;

    // Translation found, restore colons in the result
    // eslint-disable-next-line security/detect-non-literal-regexp -- COLON_PLACEHOLDER is an internal constant
    return match.replace(new RegExp(this.COLON_PLACEHOLDER, 'g'), ':');
  }

  private resolveNonStringInput(id: any): string {
    if (id.message) return this.localizeId(id.message);
    if (id.detail) {
      return this.localizeId(
        typeof id.detail === 'string' ? id.detail : JSON.stringify(id.detail)
      );
    }
    if (typeof id === 'object') {
      const strings = this.extractNestedStrings(id);
      if (strings.length > 0) return strings.join(', ');
    }
    return String(id);
  }

  /** Returns translated string, or null if no translation found */
  private tryTranslationWithFallback(sanitized: string): string | null {
    const tryKey = (key: string) =>
      ($localize as any)({ 0: `:@@${key}:${key}`, raw: [':'] });

    const match = tryKey(sanitized);
    if (match !== sanitized) return match;

    const upper = sanitized.toUpperCase();
    const upperMatch = tryKey(upper);
    if (upperMatch !== upper) return upperMatch;

    const lower = sanitized.toLowerCase();
    const lowerMatch = tryKey(lower);
    if (lowerMatch !== lower) return lowerMatch;

    return null;
  }

  private extractNestedStrings(obj: unknown): string[] {
    if (typeof obj === 'string') return [obj];
    if (Array.isArray(obj))
      return obj.flatMap((item) => this.extractNestedStrings(item));
    if (obj && typeof obj === 'object') {
      return Object.values(obj).flatMap((val) =>
        this.extractNestedStrings(val)
      );
    }
    return [];
  }
}
